import { useSyncExternalStore } from 'react'
import { z } from './zod'

// localStorage key holding the location the visitor explicitly chose in the
// location picker (VIT-17740).
//
// Distinct from GEO_OVERRIDE_KEY in use-geo.ts, which is the team's debug
// override (/__geo) and must keep winning over a real pick so the debug page
// still works while one is stored.
export const PICKED_LOCATION_KEY = 'atlas-location'

// Event dispatched on write so every mounted usePickedLocation re-reads. A
// same-tab localStorage write fires no `storage` event (that one is cross-tab
// only), so the store needs its own channel.
const PICKED_LOCATION_EVENT = 'atlas-location-change'

const PickedLocationSchema = z.object({
  // The ZIP chosen in the typeahead. Drives the `zip-#####` URL form and
  // `address.postalCode`. Null when the visitor only granted GPS.
  postalCode: z
    .string()
    .regex(/^\d{5}$/)
    .nullable()
    .default(null),
  // City/state carried by the chosen ZIP suggestion — display label only, and
  // absent for the seed rows whose city/state are unknown.
  city: z.string().nullable().default(null),
  region: z.string().nullable().default(null),
  // Browser-GPS coordinates. No production caller writes these any more: the
  // picker's "Use current location" row is hidden until an endpoint
  // reverse-geocodes a point to a ZIP (VIT-17931). buildPickedLocation still
  // takes `coords`, so the path stays unit-tested and reinstating the row needs
  // no schema change; picks stored before this still parse.
  coords: z.object({ latitude: z.number(), longitude: z.number() }).nullable().default(null),
})

export type PickedLocation = z.infer<typeof PickedLocationSchema>

// Malformed JSON or a non-conforming shape yields null (no pick) rather than
// throwing — a hand-edited or stale storage value must never break the page.
// Mirrors parseGeoOverride in use-geo.ts.
export const parsePickedLocation = (raw: string | null): PickedLocation | null => {
  if (raw == null) return null
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return null
  }
  const result = PickedLocationSchema.safeParse(json)
  return result.success ? result.data : null
}

// Normalise the parts the picker collects into the stored shape. Pure — no
// storage access — so the write helpers stay thin (mirrors buildGeoOverride).
export const buildPickedLocation = (fields: {
  postalCode?: string | null | undefined
  city?: string | null | undefined
  region?: string | null | undefined
  coords?: { latitude: number; longitude: number } | null | undefined
}): PickedLocation =>
  PickedLocationSchema.parse({
    postalCode: fields.postalCode ?? null,
    city: fields.city ?? null,
    region: fields.region ?? null,
    coords: fields.coords ?? null,
  })

// Every storage call is wrapped: getItem/setItem/removeItem all throw when
// storage is disabled by browser policy (or on quota), and none of that should
// surface to the visitor. The booleans let a caller report the failure.
const readRaw = (): string | null => {
  try {
    return localStorage.getItem(PICKED_LOCATION_KEY)
  } catch {
    return null
  }
}

export const readPickedLocation = (): PickedLocation | null => parsePickedLocation(readRaw())

const notify = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(PICKED_LOCATION_EVENT))
}

export const writePickedLocation = (picked: PickedLocation): boolean => {
  try {
    localStorage.setItem(PICKED_LOCATION_KEY, JSON.stringify(picked))
  } catch {
    return false
  }
  notify()
  return true
}

export const clearPickedLocation = (): boolean => {
  try {
    localStorage.removeItem(PICKED_LOCATION_KEY)
  } catch {
    return false
  }
  notify()
  return true
}

export const subscribeToPickedLocation = (listener: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(PICKED_LOCATION_EVENT, listener)
  // Cross-tab: a pick made in another tab should reach this one too.
  window.addEventListener('storage', listener)
  return () => {
    window.removeEventListener(PICKED_LOCATION_EVENT, listener)
    window.removeEventListener('storage', listener)
  }
}

// useSyncExternalStore requires getSnapshot to be referentially stable between
// notifications — parsing on every call would return a fresh object each render
// and loop forever. Cache against the raw string and only re-parse when it
// actually changes.
let cachedRaw: string | null = null
let cachedValue: PickedLocation | null = null

const getSnapshot = (): PickedLocation | null => {
  const raw = readRaw()
  if (raw !== cachedRaw) {
    cachedRaw = raw
    cachedValue = parsePickedLocation(raw)
  }
  return cachedValue
}

// localStorage doesn't exist during SSR (and the landing HTML is shared-cached
// anyway), so the server snapshot is always "no pick" — the client fills it in
// on hydration, matching how useGeo defers to the client.
const getServerSnapshot = (): PickedLocation | null => null

// An external store rather than useState + an effect so a pick re-renders the
// hero and the search form immediately, without the window.location.reload()
// that /__geo uses for the debug override.
export const usePickedLocation = (): PickedLocation | null =>
  useSyncExternalStore(subscribeToPickedLocation, getSnapshot, getServerSnapshot)
